import { useEffect, useRef, useState } from 'react';

import { useSoyuzClientConfig } from '@spotahome/soyuz/client';
import SoyuzAnalytics from '@spotahome/soyuz-analytics';

import debounce from '../../utils/debounce';

import { normalizeLocalityToCityId } from './utils';

const generateSessionToken = () =>
  `${Date.now()}-${Math.random().toString(36).slice(2)}`;

const useAutocompleteService = ({
  countryCode,
  boundCoords,
  pointOfInterest,
  onPlaceSelected
}) => {
  const { mapboxAccessToken } = useSoyuzClientConfig();
  const sessionTokenRef = useRef(generateSessionToken());
  const placesRef = useRef(null);
  const [inputValue, setInputValue] = useState(pointOfInterest?.name);
  const [suggestionList, setSuggestionList] = useState([]);
  const [suggestionDescriptions, setSuggestionDescriptions] = useState([]);
  const placesMapRef = useRef({});

  const closeSuggestions = e => {
    if (placesRef.current && !placesRef.current.contains(e.target)) {
      setSuggestionList([]);
      setSuggestionDescriptions([]);
    }
  };

  useEffect(() => {
    document.addEventListener('click', closeSuggestions);

    return () => document.removeEventListener('click', closeSuggestions);
  }, []);

  useEffect(() => {
    setInputValue(pointOfInterest ? pointOfInterest.name : '');
  }, [pointOfInterest]);

  const requestSuggestions = useRef(
    debounce(async value => {
      if (value.length > 2 && mapboxAccessToken) {
        SoyuzAnalytics.sendGA4Event('map-places', {
          provider: 'mapbox',
          input: value
        });
        try {
          const params = new URLSearchParams({
            q: value,
            access_token: mapboxAccessToken,
            session_token: sessionTokenRef.current,
            limit: '5',
            language: document.documentElement.lang || 'en'
          });

          if (boundCoords) {
            params.set(
              'proximity',
              `${parseFloat(boundCoords.lng)},${parseFloat(boundCoords.lat)}`
            );
          }

          if (countryCode) {
            params.set('country', countryCode);
          }

          const response = await fetch(
            `https://api.mapbox.com/search/searchbox/v1/suggest?${params}`
          );
          const data = await response.json();

          if (data.suggestions && data.suggestions.length > 0) {
            const newPlacesMap = {};
            const formattedResults = data.suggestions.map(suggestion => {
              const description =
                suggestion.full_address ||
                suggestion.place_formatted ||
                suggestion.name;
              newPlacesMap[description] = suggestion;
              return {
                mapbox_id: suggestion.mapbox_id,
                description,
                place_id: suggestion.mapbox_id,
                structured_formatting: {
                  main_text: suggestion.name,
                  secondary_text: suggestion.place_formatted || '',
                  main_text_matched_substrings: [
                    { offset: 0, length: value.length }
                  ]
                }
              };
            });
            placesMapRef.current = newPlacesMap;
            setSuggestionList(formattedResults);
            setSuggestionDescriptions(formattedResults.map(r => r.description));
          } else {
            placesMapRef.current = {};
            setSuggestionList([]);
            setSuggestionDescriptions([]);
          }
        } catch {
          placesMapRef.current = {};
          setSuggestionList([]);
          setSuggestionDescriptions([]);
        }
      } else {
        placesMapRef.current = {};
        setSuggestionList([]);
        setSuggestionDescriptions([]);
      }
    }, 300)
  );

  const handleInput = e => {
    const value = e?.target?.value ?? e;
    setInputValue(value);
    requestSuggestions.current(value);
  };

  const setSelectedValue = placeSelected => {
    setInputValue(placeSelected.description || placeSelected.name);
    setSuggestionList([]);
    setSuggestionDescriptions([]);
    placesMapRef.current = {};

    onPlaceSelected(placeSelected);
  };

  const retrievePlace = async mapboxId => {
    const params = new URLSearchParams({
      access_token: mapboxAccessToken,
      session_token: sessionTokenRef.current
    });

    const response = await fetch(
      `https://api.mapbox.com/search/searchbox/v1/retrieve/${mapboxId}?${params}`
    );
    return response.json();
  };

  const handleSelect = (placeId, description) => async () => {
    if (placeId) {
      const data = await retrievePlace(placeId);
      const feature = data.features?.[0];

      if (feature) {
        const [lng, lat] = feature.geometry.coordinates;
        const context = feature.properties?.context || {};
        const locality = context.place?.name || context.locality?.name || '';
        const cityId = normalizeLocalityToCityId(locality);

        setSelectedValue({
          placeId,
          cityId,
          lat,
          lng,
          name: description
        });

        sessionTokenRef.current = generateSessionToken();
      }
    }
  };

  const handlePlaceSelected = async selectedDescription => {
    const suggestion = placesMapRef.current[selectedDescription];

    if (suggestion && suggestion.mapbox_id) {
      const data = await retrievePlace(suggestion.mapbox_id);
      const feature = data.features?.[0];

      if (feature) {
        const [lng, lat] = feature.geometry.coordinates;
        const context = feature.properties?.context || {};
        const locality = context.place?.name || context.locality?.name || '';
        const cityId = normalizeLocalityToCityId(locality);

        const selectedPlace = {
          placeId: suggestion.mapbox_id,
          cityId,
          lat,
          lng,
          name: selectedDescription
        };

        setInputValue(selectedDescription);
        setSuggestionList([]);
        setSuggestionDescriptions([]);
        placesMapRef.current = {};

        onPlaceSelected(selectedPlace);
        sessionTokenRef.current = generateSessionToken();
      }
    }
  };

  const handleInputChange = handleInput;

  return {
    handleInput,
    handleInputChange,
    suggestionList,
    suggestionDescriptions,
    inputValue,
    placesRef,
    handleSelect,
    handlePlaceSelected,
    placesMapRef
  };
};

export default useAutocompleteService;
